<script setup lang="ts">
const emit = defineEmits<{ close: [] }>()
const props = defineProps<{ game: ManageGame, availableWriters: Player[], defaultWriter?: number | undefined }>()

const { callAction } = useTournamentActions()

const search = ref('')
const selectedWriter = ref<number | null>(props.defaultWriter || props.game.writer?.id || null)

const { t } = useI18n()

async function selectWriter(writerId: number | null) {
  if (!props.game.tournament_id) {
    return
  }

  await callAction(props.game.tournament_id, 'update-writer', {
    game_id: props.game.id,
    writer_id: writerId,
  }).then(() => {
    emit('close')
  }).catch(() => {
    useSonner.error(t('Could not update writer'))
  })
}

const filteredWriters = computed(() => {
  if (!search.value) {
    return props.availableWriters
  }
  const searchLower = search.value.toLowerCase()
  return props.availableWriters.filter((writer) => {
    const fullName = `${writer?.name || ''}`
    return fullName.toLowerCase().includes(searchLower)
  })
})
</script>

<template>
  <UiDialog
    :open="true"
    @update:open="emit('close')"
  >
    <UiDialogContent
      :title="$t('Select writer')"
      :description="game.label"
    >
      <UiAlert
        v-if="defaultWriter"
        variant="warning"
        :title="$t('Previous loser on this board has been pre-selected')"
      />
      <div class="space-y-4">
        <UiInput
          v-model="search"
          :placeholder="$t('Search by name...')"
          type="text"
          class="w-full"
        />

        <div class="max-h-96 overflow-y-auto rounded-lg border">
          <div
            v-for="writer in filteredWriters"
            :key="writer!.id"
            class="cursor-pointer border-b p-3 transition-colors hover:bg-accent"
            :class="{ 'bg-accent': selectedWriter === writer!.id }"
            @click="selectWriter(writer!.id)"
          >
            <div class="flex flex-col">
              <span class="font-medium">
                {{ writer!.name }}
              </span>
            </div>
          </div>

          <div
            v-if="filteredWriters.length === 0"
            class="p-4 text-center text-muted-foreground"
          >
            {{ $t('No players found') }}
          </div>
        </div>
      </div>

      <template #footer>
        <UiDialogFooter>
          <UiButton
            v-if="game.writer?.id"
            variant="destructive"
            :text="$t('Remove writer')"
            @click="selectWriter(null)"
          />
          <UiButton
            variant="outline"
            :text="$t('Close')"
            @click="emit('close')"
          />
          <UiButton
            v-if="defaultWriter"
            variant="success"
            :text="$t('Confirm')"
            @click="selectWriter(defaultWriter)"
          />
        </UiDialogFooter>
      </template>
    </UiDialogContent>
  </UiDialog>
</template>
